// OnboardingStep5.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { submitAccessibilityAtom } from "@/store/atoms";
import {
  useAuth,
  useAccessibility,
  useI18N,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { createPageAnalytics } from "common/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useDarkMode } from "@/hooks/useDarkMode";
import WideButton from "@/components/common/WideButton";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { ArrowLeft } from "lucide-react";
import { StandardButton } from "@/components/common/StandardButton";
import SelectOption from "@/components/common/SelectOption";

interface OnboardingStep5Props {
  onClose: () => void;
  title: string;
  subtitle: string;
  onSkip: () => void;
}

const OnboardingStep5 = ({
  onClose,
  title,
  subtitle,
}: OnboardingStep5Props) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  const { user } = useAuth();
  const isDark = useDarkMode();
  //set index for swipable views
  const { setScreenIndex } = useAuthModals();
  const profileSetupAnalytics = createPageAnalytics("profile_setup");
  const { modalStyle, modalClassName } = useModalHeight();
  //fetch data options to display
  const { howDoYouHearAboutOptions } = useAccessibility();

  //get and store the data for entire onBoarding steps
  const [submitData, setSubmitData] = useAtom(submitAccessibilityAtom);

  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [other, setOther] = useState("");

  useEffect(() => {
    profileSetupAnalytics.track("page_view_page", {
      navigation_direction: "next",
      previous_step_index: 4,
      step_index: 5,
      step_name: "referral_source",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBack = async () => {
    setScreenIndex(AuthModalScreen.OnboardingStep4);
  };

  const handleOptionChange = (option: number) => {
    if (selectedOptions.includes(option)) {
      setSelectedOptions(selectedOptions.filter((o) => o !== option));
    } else {
      setSelectedOptions([...selectedOptions, option]);
    }
  };

  const onContinue = async () => {
    // Final profile-setup step on web -> wizard completion.
    profileSetupAnalytics.track("continue_action_button", { step_name: "referral_source" });

    setSubmitData((prev) => ({
      ...prev,
      how_do_you_hear_about_us_ids: selectedOptions.join(","),
    }));

    if (!user) {
      //move to next step
      setScreenIndex(AuthModalScreen.VerifyEmail);
    } else {
      onClose();
    }
  };

  const filteredOptions = howDoYouHearAboutOptions.filter(
    (e) => e.title !== "Other: Please specify",
  );

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div
      style={modalStyle}
      className="flex flex-col rounded-xl bg-bg-brand-secondary-default overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          onClick={onBack}
          variant="back"
          icon={<ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />}
          aria-label="Go back"
          tabIndex={0}
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {i18n.get("profileSetup")}
        </h1>
      </header>
      <div className="flex-1 overflow-y-auto px-8 md:px-16">
        <div className="pt-6">
          <h2 className="mb-4 text-fg-neutral-strong title-m-bold">{title}</h2>
          <p className="title-s-semibold text-fg-neutral-strong">{subtitle}</p>
          <div>
            <hr className="w-full border-border-token-brand-minimal my-3" />
          </div>
        </div>
        <p className="title-s-semibold text-fg-neutral-strong">
          {i18n.get("howDidYouFindOutAboutSociability")}
        </p>
        <p className="my-4 body-l-semibold !text-fg-brand-subtle">
          {i18n.get("selectAllThatApply")}
        </p>
        <div
          className="flex flex-wrap gap-3 pt-2"
          role="group"
          aria-label={i18n.get("howDidYouFindOutAboutSociability")}
        >
          {filteredOptions.map((option, index) => (
            <SelectOption
              key={index}
              type="checkbox"
              name="how-did-you-hear"
              checked={selectedOptions.includes(option.id)}
              onChange={() => handleOptionChange(option.id)}
            >
              {option.title}
            </SelectOption>
          ))}
          <div className="w-full pb-4">
            <FloatingLabelInput
              label="Other"
              type="text"
              value={other}
              placeholder="Other"
              onChange={(event) => {
                setOther(event.target.value);
              }}
              onClear={() => {
                setOther("");
              }}
            />
          </div>
        </div>
        <div className="mb-8 items-center justify-center flex flex-col pt-2">
          <WideButton
            disabled={selectedOptions.length <= 0 && other.length <= 0}
            type="filled"
            onClick={() => onContinue()}
          >
            {i18n.get("continue")}
          </WideButton>
          <StandardButton
            onClick={onContinue}
            variant="link"
            className="body-l-semibold underline text-links mt-6"
          >
            {i18n.get("skipForNow")}
          </StandardButton>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep5;
